import { FOLLOWUP_PRESETS, followupPresetDate, formatDate, formatTime, formatWeekday, israelDateTime, israelDay, type FollowupPreset } from '@takumi/shared';
import { useState } from 'react';
import { Input } from '@/components/ui/form';
import { cn } from '@/lib/utils';

export interface ScheduleValue {
  preset: FollowupPreset | 'custom' | 'none';
  dueAt: Date | null;
}

/**
 * Preset-first follow-up scheduling: מחר / בעוד 3 ימים / שבוע / שבועיים / חודש / תאריך אחר.
 * `allowNone` shows an explicit "ללא פולואפ" choice (the UI then warns that nothing is scheduled).
 */
export function FollowupScheduler({ value, onChange, allowNone }: { value: ScheduleValue; onChange: (v: ScheduleValue) => void; allowNone?: boolean }) {
  const [customDay, setCustomDay] = useState(() => israelDay(followupPresetDate('tomorrow')));
  const [customTime, setCustomTime] = useState('10:00');

  const setCustom = (day: string, time: string) => {
    const [h, m] = time.split(':').map(Number);
    onChange({ preset: 'custom', dueAt: day ? israelDateTime(day, h ?? 10, m ?? 0) : null });
  };

  const chip = (active: boolean) =>
    cn(
      'rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors',
      active ? 'border-matcha-600 bg-matcha-700 text-white' : 'border-line-strong bg-surface text-ink-soft hover:border-matcha-400 hover:bg-matcha-50',
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="מועד הפולואפ הבא">
        {FOLLOWUP_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={value.preset === p.key}
            className={chip(value.preset === p.key)}
            onClick={() => onChange({ preset: p.key, dueAt: followupPresetDate(p.key) })}
          >
            {p.label}
          </button>
        ))}
        <button type="button" role="radio" aria-checked={value.preset === 'custom'} className={chip(value.preset === 'custom')} onClick={() => setCustom(customDay, customTime)}>
          תאריך אחר
        </button>
        {allowNone && (
          <button
            type="button"
            role="radio"
            aria-checked={value.preset === 'none'}
            className={cn(chip(value.preset === 'none'), value.preset === 'none' && 'border-overdue bg-overdue')}
            onClick={() => onChange({ preset: 'none', dueAt: null })}
          >
            ללא פולואפ
          </button>
        )}
      </div>
      {value.preset === 'custom' && (
        <div className="flex gap-2">
          <Input
            type="date"
            aria-label="תאריך"
            value={customDay}
            min={israelDay()}
            onChange={(e) => {
              setCustomDay(e.target.value);
              setCustom(e.target.value, customTime);
            }}
            className="w-44"
          />
          <Input
            type="time"
            aria-label="שעה"
            value={customTime}
            step={900}
            onChange={(e) => {
              setCustomTime(e.target.value);
              setCustom(customDay, e.target.value);
            }}
            className="w-28"
          />
        </div>
      )}
      {value.dueAt && (
        <p className="text-sm text-muted">
          הפולואפ הבא: <span className="font-semibold text-ink">{formatWeekday(value.dueAt)} {formatDate(value.dueAt)} בשעה {formatTime(value.dueAt)}</span>
        </p>
      )}
      {value.preset === 'none' && (
        <p className="rounded-control bg-overdue-bg px-3 py-2 text-sm font-medium text-overdue">
          לא ייקבע פולואפ — הרשומה תסומן "אין פולואפ עתידי" ב"היום שלי".
        </p>
      )}
    </div>
  );
}

export const defaultSchedule = (): ScheduleValue => ({ preset: 'in3days', dueAt: followupPresetDate('in3days') });
